/* DashboardPage - Guide list within a CARD - v2.0 - Sections moved to AdminCardListPage sidebar */
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  BookOpen, Plus, MoreVertical, Copy, ExternalLink, Trash2, 
  LogOut, Settings, Loader2, CheckCircle2, XCircle, Search, MessageCircle, Clock, Send, HelpCircle, Timer, FileUp, FolderOpen, ArrowLeft
} from "lucide-react";
import OnboardingWizard from "@/components/OnboardingWizard";
import PDFImportModal from "@/components/PDFImportModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to calculate resolution time
const calculateResolutionTime = (createdAt, confirmedAt) => {
  if (!createdAt || !confirmedAt) return null;
  const start = new Date(createdAt);
  const end = new Date(confirmedAt);
  const diffMs = end - start;
  
  if (diffMs < 0) return null;
  
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffDays > 0) {
    const remainingHours = diffHours % 24;
    return `${diffDays}g ${remainingHours}h`;
  } else if (diffHours > 0) {
    const remainingMins = diffMins % 60;
    return `${diffHours}h ${remainingMins}m`;
  } else {
    return `${diffMins}m`;
  }
};

// Helper to format average time
const formatAverageTime = (logs) => {
  const confirmedLogs = logs.filter(log => log.confirmed && log.confirmed_at && log.created_at);
  if (confirmedLogs.length === 0) return '--';
  
  let totalMs = 0;
  confirmedLogs.forEach(log => {
    const start = new Date(log.created_at);
    const end = new Date(log.confirmed_at);
    totalMs += (end - start);
  });
  
  const avgMs = totalMs / confirmedLogs.length;
  const avgMins = Math.floor(avgMs / 60000);
  const avgHours = Math.floor(avgMins / 60);
  const avgDays = Math.floor(avgHours / 24);
  
  if (avgDays > 0) {
    const remainingHours = avgHours % 24;
    return `${avgDays}g ${remainingHours}h`;
  } else if (avgHours > 0) {
    const remainingMins = avgMins % 60;
    return `${avgHours}h ${remainingMins}m`;
  } else {
    return `${avgMins}m`;
  }
};

export default function DashboardPage({ auth }) {
  const navigate = useNavigate();
  const { cardId } = useParams(); // Get cardId from URL if present
  
  // Check if user is admin
  const isAdmin = auth.user?.role === 'admin';
  
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newModel, setNewModel] = useState({ name: "", code: "", card_id: cardId || "" });
  
  // Current card info (when viewing a specific card)
  const [currentCard, setCurrentCard] = useState(null);
  
  // WhatsApp modal
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [whatsappName, setWhatsappName] = useState('');
  const [selectedModel, setSelectedModel] = useState(null);
  
  // Send logs for KPI stats only
  const [sendLogs, setSendLogs] = useState([]);
  
  // CARD list for dropdown
  const [cards, setCards] = useState([]);
  
  // PDF Import modal
  const [showPDFImport, setShowPDFImport] = useState(false);
  
  // Onboarding wizard
  const [showWizard, setShowWizard] = useState(() => {
    const hasSeenWizard = localStorage.getItem('guideflow_hasSeenWizard');
    return !hasSeenWizard;
  });
  
  const closeWizard = () => {
    setShowWizard(false);
    localStorage.setItem('guideflow_hasSeenWizard', 'true');
  };

  // Fetch current card info
  const fetchCurrentCard = async () => {
    if (!cardId) return;
    try {
      const res = await fetch(`${API}/admin/cards/${cardId}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentCard(data);
      }
    } catch (err) {
      console.error("Error fetching card:", err);
    }
  };

  // Fetch cards for dropdown
  const fetchCards = async () => {
    try {
      const res = await fetch(`${API}/admin/cards`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      const data = await res.json();
      setCards(data);
    } catch (err) {
      console.error("Error fetching cards:", err);
    }
  };

  const fetchModels = async () => {
    try {
      const res = await fetch(`${API}/admin/models`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (!res.ok) {
        if (res.status === 401) {
          toast.error("Sessione scaduta, effettua nuovamente il login");
          auth.logout();
          return;
        }
        throw new Error("Errore nel caricamento");
      }
      let data = await res.json();
      
      // Filter by cardId if present
      if (cardId) {
        // Find the card to check if it's the "altro" system card
        const cardInfo = cards.find(c => c.id === cardId) || currentCard;
        if (cardInfo?.is_system && cardInfo?.slug === 'altro') {
          // System card: show models WITHOUT card_id
          data = data.filter(m => !m.card_id);
        } else {
          // Regular card: show models WITH this card_id
          data = data.filter(m => m.card_id === cardId);
        }
      }
      
      setModels(data);
      // Cache models in localStorage for offline resilience
      localStorage.setItem('guideflow_models_cache', JSON.stringify(data));
    } catch (err) {
      // Try to load from cache if network fails
      const cached = localStorage.getItem('guideflow_models_cache');
      if (cached) {
        setModels(JSON.parse(cached));
        toast.info("Caricati dati dalla cache locale");
      } else {
        toast.error(err.message);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSendLogs = async (showError = false) => {
    try {
      const res = await fetch(`${API}/admin/guide-sends/logs?limit=100`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSendLogs(data);
        // Cache logs
        localStorage.setItem('guideflow_logs_cache', JSON.stringify(data));
      } else if (res.status === 401) {
        // Token expired - silent fail for background refresh
        if (showError) {
          toast.error("Sessione scaduta");
          auth.logout();
        }
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      // Load from cache on error
      const cached = localStorage.getItem('guideflow_logs_cache');
      if (cached) {
        setSendLogs(JSON.parse(cached));
      }
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchCards();
    fetchCurrentCard();
  }, [cardId]);
  
  // Fetch models after cards are loaded (to properly filter)
  useEffect(() => {
    if (cards.length > 0 || !cardId) {
      fetchModels();
    }
    fetchSendLogs();
  }, [cards, cardId]);
  
  // Update newModel.card_id when cardId changes
  useEffect(() => {
    if (cardId) {
      setNewModel(prev => ({ ...prev, card_id: cardId }));
    }
  }, [cardId]);
  
  // Silent auto-refresh for send logs (every 15 seconds for better responsiveness)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSendLogs(false); // Silent refresh, no error toast
    }, 15000); // 15 seconds
    
    return () => clearInterval(interval);
  }, [auth.token]);
  
  // Also refresh when tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchModels();
        fetchSendLogs(false);
        fetchCards();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [auth.token]);

  const handleCreateModel = async (e) => {
    e.preventDefault();
    if (!newModel.name || !newModel.code) {
      toast.error("Compila tutti i campi");
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API}/admin/models`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`
        },
        body: JSON.stringify(newModel)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Errore");
      
      setModels([...models, data]);
      setShowCreateModal(false);
      setNewModel({ name: "", code: "" });
      toast.success("Modello creato!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteModel = async (modelId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo modello?")) return;
    
    try {
      const res = await fetch(`${API}/admin/models/${modelId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (!res.ok) throw new Error("Errore nell'eliminazione");
      
      setModels(models.filter(m => m.id !== modelId));
      toast.success("Modello eliminato");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const copyGuideLink = (model) => {
    if (!model.guide?.published || !model.guide?.public_token) {
      toast.error("Pubblica la guida per generare il link");
      return;
    }
    const link = `${window.location.origin}/g/${model.guide_slug}?t=${model.guide.public_token}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copiato negli appunti!");
  };

  const openWhatsappModal = (model) => {
    if (!model.guide?.published || !model.guide?.public_token) {
      toast.error("Pubblica la guida per inviarla su WhatsApp");
      return;
    }
    setSelectedModel(model);
    setWhatsappPhone('');
    setWhatsappName('');
    setWhatsappModalOpen(true);
  };

  // Generate WhatsApp URL dynamically
  const getWhatsappUrl = () => {
    if (!whatsappPhone || !selectedModel) return '';
    
    let cleanPhone = whatsappPhone.replace(/[^\d]/g, '');
    if (cleanPhone.startsWith('0')) cleanPhone = cleanPhone.substring(1);
    if (!cleanPhone.startsWith('39')) cleanPhone = '39' + cleanPhone;
    if (cleanPhone.length < 11) return '';
    
    const customerName = whatsappName || 'Cliente';
    const guideLink = `${window.location.origin}/g/${selectedModel.guide_slug}?t=${selectedModel.guide.public_token}&phone=${cleanPhone}&name=${encodeURIComponent(customerName)}`;
    const message = `Ciao ${customerName}! Ecco la guida: ${guideLink}`;
    
    return `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(message)}`;
  };

  const logWhatsappSend = async () => {
    if (!whatsappPhone || !selectedModel) return;
    
    let cleanPhone = whatsappPhone.replace(/[^\d]/g, '');
    if (cleanPhone.startsWith('0')) cleanPhone = cleanPhone.substring(1);
    if (!cleanPhone.startsWith('39')) cleanPhone = '39' + cleanPhone;
    
    const customerName = whatsappName || 'Cliente';
    
    try {
      await fetch(`${API}/admin/guide-sends/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`
        },
        body: JSON.stringify({
          phone_number: cleanPhone,
          customer_name: customerName,
          guide_id: selectedModel.guide.id,
          guide_title: selectedModel.guide.title,
          model_name: selectedModel.name
        })
      });
      fetchSendLogs();
    } catch (e) {
      console.error('Error logging send:', e);
    }
    
    setWhatsappModalOpen(false);
    setWhatsappPhone('');
    setWhatsappName('');
    toast.success(`Apertura WhatsApp per +${cleanPhone}`);
  };

  const filteredModels = models.filter(m => 
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-bold">GuideFlow</h1>
              <p className="text-xs text-muted-foreground">Gestione Guide</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => setShowWizard(true)}
                    data-testid="help-btn"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Come usare GuideFlow</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="text-sm text-muted-foreground hidden sm:block">
              {auth.user?.username}
            </span>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => auth.logout()}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Onboarding Wizard */}
      <OnboardingWizard open={showWizard} onClose={closeWizard} />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Stats Card */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Send className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{sendLogs.length}</p>
                  <p className="text-xs text-muted-foreground">Invii Totali</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{sendLogs.filter(log => log.confirmed).length}</p>
                  <p className="text-xs text-muted-foreground">Confermati</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{sendLogs.filter(log => !log.confirmed).length}</p>
                  <p className="text-xs text-muted-foreground">In Attesa</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border-cyan-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <Timer className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatAverageTime(sendLogs)}</p>
                  <p className="text-xs text-muted-foreground">Tempo Medio</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{models.length}</p>
                  <p className="text-xs text-muted-foreground">Guide</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Card Header (when viewing a specific card) */}
        {cardId && currentCard && (
          <div className="flex items-center gap-4 mb-6 p-4 bg-muted/50 rounded-lg">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: (currentCard.color || '#6366f1') + '20' }}
            >
              <FolderOpen className="w-6 h-6" style={{ color: currentCard.color || '#6366f1' }} />
            </div>
            <div>
              <h2 className="text-xl font-bold">{currentCard.name}</h2>
              {currentCard.description && (
                <p className="text-sm text-muted-foreground">{currentCard.description}</p>
              )}
            </div>
          </div>
        )}

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">{cardId && currentCard ? 'Guide' : 'Modelli'}</h2>
            <p className="text-muted-foreground mt-1">
              {models.length} modelli configurati
            </p>
          </div>
          
          <div className="flex gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-initial">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca modelli..."
                className="pl-9 w-full sm:w-64"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-models-input"
              />
            </div>
            
            {/* PDF Import Button - Admin only */}
            {isAdmin && (
              <Button 
                variant="outline" 
                onClick={() => setShowPDFImport(true)}
                data-testid="import-pdf-btn"
              >
                <FileUp className="w-4 h-4 mr-2" />
                Importa PDF
              </Button>
            )}
            
            {/* Create Model - Admin only */}
            {isAdmin && (
              <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogTrigger asChild>
                  <Button className="rounded-full" data-testid="create-model-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nuovo Modello</DialogTitle>
                    <DialogDescription>
                      Crea un nuovo modello per associarvi una guida
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateModel}>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="model-name">Nome modello</Label>
                        <Input
                          id="model-name"
                          data-testid="model-name-input"
                          placeholder="Es: Cassa X v3"
                          value={newModel.name}
                          onChange={(e) => setNewModel({ ...newModel, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                      <Label htmlFor="model-code">Codice univoco</Label>
                      <Input
                        id="model-code"
                        data-testid="model-code-input"
                        placeholder="Es: CASSA_X_V3"
                        value={newModel.code}
                        onChange={(e) => setNewModel({ ...newModel, code: e.target.value.toUpperCase().replace(/\s/g, "_") })}
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        Usato per generare lo slug della guida
                      </p>
                    </div>
                    {cards.length > 0 && (
                    <div className="space-y-2">
                      <Label htmlFor="model-card">Card (opzionale)</Label>
                      <Select 
                        value={newModel.card_id || "none"} 
                        onValueChange={(value) => setNewModel({ ...newModel, card_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger data-testid="model-card-select">
                          <SelectValue placeholder="Nessuna card" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nessuna card</SelectItem>
                          {cards.map((card) => (
                            <SelectItem key={card.id} value={card.id}>
                              {card.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Assegna la guida a una categoria (puoi farlo anche dopo)
                      </p>
                    </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                      Annulla
                    </Button>
                    <Button type="submit" disabled={creating} data-testid="submit-create-model-btn">
                      {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Crea modello
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        {/* Models Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredModels.length === 0 ? (
          <Card className="text-center py-20">
            <CardContent>
              <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {searchQuery ? "Nessun risultato" : "Nessun modello"}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery ? "Prova con una ricerca diversa" : "Crea il tuo primo modello per iniziare"}
              </p>
              {!searchQuery && (
                <Button onClick={() => setShowCreateModal(true)} className="rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Crea modello
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredModels.map((model, index) => (
              <Card 
                key={model.id} 
                className="card-interactive animate-fadeIn"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`model-card-${model.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">{model.name}</CardTitle>
                      <CardDescription className="font-mono text-xs mt-1">
                        {model.code}
                      </CardDescription>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="shrink-0" data-testid={`model-menu-${model.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {isAdmin && (
                          <DropdownMenuItem onClick={() => navigate(`/admin/models/${model.id}`)}>
                            <Settings className="w-4 h-4 mr-2" />
                            Modifica guida
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => copyGuideLink(model)}
                          disabled={!model.guide?.published}
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copia link guida
                        </DropdownMenuItem>
                        {model.guide?.published && model.guide?.public_token && (
                          <DropdownMenuItem 
                            onClick={() => window.open(`/g/${model.guide_slug}?t=${model.guide.public_token}`, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Apri guida
                          </DropdownMenuItem>
                        )}
                        {isAdmin && (
                          <DropdownMenuItem 
                            className="text-destructive focus:text-destructive"
                            onClick={() => handleDeleteModel(model.id)}
                            data-testid={`delete-model-${model.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Elimina
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 mb-4">
                    {model.active ? (
                      <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Attivo
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-muted text-muted-foreground">
                        <XCircle className="w-3 h-3 mr-1" />
                        Inattivo
                      </Badge>
                    )}
                    {model.guide?.published ? (
                      <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                        Guida pubblicata
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        Bozza
                      </Badge>
                    )}
                    {model.card_id && cards.find(c => c.id === model.card_id) && (
                      <Badge 
                        variant="outline" 
                        style={{ 
                          backgroundColor: cards.find(c => c.id === model.card_id)?.color + '15',
                          borderColor: cards.find(c => c.id === model.card_id)?.color + '50',
                          color: cards.find(c => c.id === model.card_id)?.color
                        }}
                      >
                        <FolderOpen className="w-3 h-3 mr-1" />
                        {cards.find(c => c.id === model.card_id)?.name}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    {isAdmin && (
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => navigate(`/admin/models/${model.id}`)}
                        data-testid={`edit-model-${model.id}`}
                      >
                        Modifica
                      </Button>
                    )}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant={model.guide?.published ? "default" : "secondary"}
                            size="icon"
                            onClick={() => copyGuideLink(model)}
                            disabled={!model.guide?.published}
                            data-testid={`copy-link-${model.id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {model.guide?.published 
                            ? "Copia link guida" 
                            : "Pubblica la guida per generare il link"
                          }
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant={model.guide?.published ? "default" : "secondary"}
                            size="icon"
                            onClick={() => openWhatsappModal(model)}
                            disabled={!model.guide?.published}
                            className={model.guide?.published ? "bg-green-600 hover:bg-green-700 text-white" : ""}
                            data-testid={`whatsapp-${model.id}`}
                          >
                            <MessageCircle className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {model.guide?.published 
                            ? "Invia su WhatsApp" 
                            : "Pubblica la guida per inviarla"
                          }
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* WhatsApp Modal */}
      <Dialog open={whatsappModalOpen} onOpenChange={setWhatsappModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-600" />
              Invia su WhatsApp
            </DialogTitle>
            <DialogDescription>
              Invia il link della guida &quot;{selectedModel?.guide?.title || selectedModel?.name}&quot; su WhatsApp
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="wa-phone">Numero di telefono *</Label>
              <Input
                id="wa-phone"
                data-testid="wa-phone-input"
                placeholder="Es: 3331234567"
                value={whatsappPhone}
                onChange={(e) => setWhatsappPhone(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Solo il numero italiano (il prefisso 39 viene aggiunto automaticamente)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="wa-name">Nome cliente (opzionale)</Label>
              <Input
                id="wa-name"
                data-testid="wa-name-input"
                placeholder="Es: Mario"
                value={whatsappName}
                onChange={(e) => setWhatsappName(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setWhatsappModalOpen(false)}>
              Annulla
            </Button>
            {getWhatsappUrl() ? (
              <a
                href={getWhatsappUrl()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={logWhatsappSend}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 bg-green-600 hover:bg-green-700 text-white"
                data-testid="send-wa-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Apri WhatsApp
              </a>
            ) : (
              <Button 
                disabled
                className="bg-green-600"
                data-testid="send-wa-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Inserisci numero
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* PDF Import Modal */}
      <PDFImportModal
        open={showPDFImport}
        onClose={() => setShowPDFImport(false)}
        auth={auth}
        models={models}
        onSuccess={(modelId) => {
          fetchModels();
          if (modelId) {
            navigate(`/admin/models/${modelId}`);
          }
        }}
      />
    </div>
  );
}
